import {
  fontSizes,
  lineHeights,
  fontWeights,
  letterSpacings,
  fontFamilies,
} from "../../primitives/typography";
import type { TypographyTheme, FontStyles } from "../../semantic/typography";
import { NEUTRAL } from "../../primitives/colors";

const createStyles = (
  role: keyof typeof fontSizes,
  size: "large" | "medium" | "small",
  weight: keyof typeof fontWeights,
  letterSpacing: number = letterSpacings.normal,
  lineHeightOverride?: number,
): FontStyles => ({
  color: NEUTRAL[10],
  fontFamily: fontFamilies.default,
  fontSize: fontSizes[role][size],
  lineHeight: lineHeightOverride ?? lineHeights[role][size],
  fontWeight: fontWeights[weight],
  letterSpacing,
});

export const baseTypography: TypographyTheme = {
  display: {
    large: {
      regular: createStyles(
        "display",
        "large",
        "medium",
        letterSpacings.tight,
        lineHeights.display.large,
      ),
      emphasized: createStyles(
        "display",
        "large",
        "semiBold",
        letterSpacings.tight,
      ),
      bold: createStyles("display", "large", "bold", letterSpacings.tight),
    },
    medium: {
      regular: createStyles(
        "display",
        "medium",
        "medium",
        letterSpacings.normal,
      ),
      emphasized: createStyles(
        "display",
        "medium",
        "semiBold",
        letterSpacings.normal,
      ),
      bold: createStyles("display", "medium", "bold", letterSpacings.normal),
    },
    small: {
      regular: createStyles(
        "display",
        "small",
        "medium",
        letterSpacings.normal,
      ),
      emphasized: createStyles(
        "display",
        "small",
        "semiBold",
        letterSpacings.normal,
      ),
      bold: createStyles("display", "small", "bold", letterSpacings.normal),
    },
  },
  headline: {
    large: {
      regular: createStyles(
        "headline",
        "large",
        "medium",
        letterSpacings.normal,
        lineHeights.headline.large,
      ),
      emphasized: createStyles(
        "headline",
        "large",
        "semiBold",
        letterSpacings.normal,
        lineHeights.headline.large,
      ),
      bold: createStyles(
        "headline",
        "large",
        "bold",
        letterSpacings.normal,
        lineHeights.headline.large,
      ),
    },
    medium: {
      regular: createStyles(
        "headline",
        "medium",
        "medium",
        letterSpacings.normal,
        lineHeights.headline.medium,
      ),
      emphasized: createStyles(
        "headline",
        "medium",
        "semiBold",
        letterSpacings.normal,
        lineHeights.headline.medium,
      ),
      bold: createStyles(
        "headline",
        "medium",
        "bold",
        letterSpacings.normal,
        lineHeights.headline.medium,
      ),
    },
    small: {
      regular: createStyles(
        "headline",
        "small",
        "medium",
        letterSpacings.normal,
        lineHeights.headline.small,
      ),
      emphasized: createStyles(
        "headline",
        "small",
        "semiBold",
        letterSpacings.normal,
        lineHeights.headline.small,
      ),
      bold: createStyles(
        "headline",
        "small",
        "bold",
        letterSpacings.normal,
        lineHeights.headline.small,
      ),
    },
  },
  title: {
    large: {
      regular: createStyles(
        "title",
        "large",
        "regular",
        letterSpacings.normal,
        lineHeights.title.large,
      ),
      emphasized: createStyles(
        "title",
        "large",
        "medium",
        letterSpacings.normal,
        lineHeights.title.large,
      ),
      bold: createStyles(
        "title",
        "large",
        "bold",
        letterSpacings.normal,
        lineHeights.title.large,
      ),
    },
    medium: {
      regular: createStyles(
        "title",
        "medium",
        "medium",
        letterSpacings.wider,
        lineHeights.title.medium,
      ),
      emphasized: createStyles(
        "title",
        "medium",
        "semiBold",
        letterSpacings.wider,
        lineHeights.title.medium,
      ),
      bold: createStyles(
        "title",
        "medium",
        "bold",
        letterSpacings.wider,
        lineHeights.title.medium,
      ),
    },
    small: {
      regular: createStyles(
        "title",
        "small",
        "medium",
        letterSpacings.wide,
        lineHeights.title.small,
      ),
      emphasized: createStyles(
        "title",
        "small",
        "semiBold",
        letterSpacings.wide,
        lineHeights.title.small,
      ),
      bold: createStyles(
        "title",
        "small",
        "semiBold",
        letterSpacings.wide,
        lineHeights.title.small,
      ),
    },
  },
  label: {
    large: {
      regular: createStyles(
        "label",
        "large",
        "medium",
        letterSpacings.wide,
        lineHeights.label.large,
      ),
      emphasized: createStyles(
        "label",
        "large",
        "semiBold",
        letterSpacings.wide,
        lineHeights.label.large,
      ),
      bold: createStyles(
        "label",
        "large",
        "semiBold",
        letterSpacings.wide,
        lineHeights.label.large,
      ),
    },
    medium: {
      regular: createStyles(
        "label",
        "medium",
        "medium",
        letterSpacings.extraWide,
        lineHeights.label.medium,
      ),
      emphasized: createStyles(
        "label",
        "medium",
        "semiBold",
        letterSpacings.extraWide,
        lineHeights.label.medium,
      ),
      bold: createStyles(
        "label",
        "medium",
        "semiBold",
        letterSpacings.extraWide,
        lineHeights.label.medium,
      ),
    },
    small: {
      regular: createStyles(
        "label",
        "small",
        "medium",
        letterSpacings.extraWide,
        lineHeights.label.small,
      ),
      emphasized: createStyles(
        "label",
        "small",
        "semiBold",
        letterSpacings.extraWide,
        lineHeights.label.small,
      ),
      bold: createStyles(
        "label",
        "small",
        "semiBold",
        letterSpacings.extraWide,
        lineHeights.label.small,
      ),
    },
  },
  body: {
    large: {
      regular: createStyles(
        "body",
        "large",
        "regular",
        letterSpacings.extraWide,
        lineHeights.body.large,
      ),
      emphasized: createStyles(
        "body",
        "large",
        "medium",
        letterSpacings.extraWide,
        lineHeights.body.large,
      ),
      bold: createStyles(
        "body",
        "large",
        "semiBold",
        letterSpacings.extraWide,
        lineHeights.body.large,
      ),
    },
    medium: {
      regular: createStyles(
        "body",
        "medium",
        "regular",
        letterSpacings.widest,
        lineHeights.body.medium,
      ),
      emphasized: createStyles(
        "body",
        "medium",
        "medium",
        letterSpacings.widest,
        lineHeights.body.medium,
      ),
      bold: createStyles(
        "body",
        "medium",
        "semiBold",
        letterSpacings.widest,
        lineHeights.body.medium,
      ),
    },
    small: {
      regular: createStyles(
        "body",
        "small",
        "regular",
        letterSpacings.relaxed,
        lineHeights.body.small,
      ),
      emphasized: createStyles(
        "body",
        "small",
        "medium",
        letterSpacings.relaxed,
        lineHeights.body.small,
      ),
      bold: createStyles(
        "body",
        "small",
        "semiBold",
        letterSpacings.relaxed,
        lineHeights.body.small,
      ),
    },
  },
};
